import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { ReducerStates } from "../../helpers/constants";
import { IFailedRequest, loginServices } from "../../services/login.services";
import { dashboardServices } from "../../services/dashboard.services";
import { initialize } from "../app/asyncThunkActions";
import store from "../../helpers/store";
import { IInvoiceData } from "../../services/dashboard.services";
import { StorageKeys } from "../../services/constants";
import { clearSession } from "../dashboard/dashboardSlice";

export interface ILoginState {
  isLogged: boolean;
  state: ReducerStates;
  errorCode: string;
  errorMessage: string;
  mode: string;
  authParams: string;
  origin?: string;
  parentClientID?: string;
  loginEmailSent: boolean;
  email: string;
}

export const initialLoginState: ILoginState = {
  isLogged: false,
  state: ReducerStates.IDLE,
  errorCode: "",
  errorMessage: "",
  mode: "",
  authParams: "",
  origin: undefined,
  parentClientID: undefined,
  loginEmailSent: false,
  email: "",
};

export const LoginModes = {
  ALEXA: "alexa",
  LOGIN: "login",
};

export const getClientID = () => {
  const parentClientID = store.getState().loginState.parentClientID;
  if (parentClientID !== undefined) {
    return parentClientID;
  } else {
    return (process.env.REACT_APP_CLIENT_ID as unknown) as string;
  }
};

export const emailLogin = createAsyncThunk(
  "stripe/emailLogin",
  async (email: string) => {
    const subscriptionData = await loginServices.sendLoginEmail(email);
    return subscriptionData;
  }
);

// Get current subscription info
export const getSubscriptionInfo = createAsyncThunk(
  "stripe/getSubscriptionInfo",
  async (email: string) => {
    const subscriptionData = await dashboardServices.getSubscriptionInfo(email);
    return subscriptionData;
  }
);

// Get upcoming invoice
export const getUpcomingInvoice = createAsyncThunk(
  "stripe/getUpcomingInvoice",
  async (invoice: IInvoiceData[]) => {
    const subscriptionData = await dashboardServices.getUpcomingInvoice(
      invoice
    );
    return subscriptionData;
  }
);

const loginSlice = createSlice({
  name: "login",
  initialState: initialLoginState,
  reducers: {
    logout(state) {
      state.isLogged = initialLoginState.isLogged;
      state.errorCode = initialLoginState.errorCode;
      state.errorMessage = initialLoginState.errorMessage;
      state.state = ReducerStates.IDLE;
      state.loginEmailSent = false;
      state.email = "";
      localStorage.removeItem(StorageKeys.USER);
      localStorage.removeItem(StorageKeys.SUBSCRIPTION_DATA);
      localStorage.removeItem(StorageKeys.SELECTED_SUBSCRIPTION);
    },
    subscriptionManagementInit(state) {
      state.state = ReducerStates.IDLE;
      state.errorCode = initialLoginState.errorCode;
      state.errorMessage = initialLoginState.errorMessage;
    },
    resetSliceState(state) {
      state.state = ReducerStates.IDLE;
    },
    authorizeMode(state, action) {
      state.mode = action.payload.mode;
      state.authParams = action.payload.authParams;
    },
    setFrameOrigin(state, action: PayloadAction<string>) {
      state.origin = action.payload;
    },
    setLoginEmail(state, action: PayloadAction<string>) {
      state.email = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getSubscriptionInfo.pending, (state) => {
        state.state = ReducerStates.PENDING;
        state.errorCode = initialLoginState.errorCode;
        state.errorMessage = initialLoginState.errorMessage;
      })
      .addCase(getSubscriptionInfo.fulfilled, (state, action) => {
        state.isLogged = true;
        state.loginEmailSent = false;
        state.state = ReducerStates.SUCCEEDED;
      })
      .addCase(getSubscriptionInfo.rejected, (state, action) => {
        const error = (action.error as unknown) as IFailedRequest;
        state.errorCode = error?.code || "";
        state.errorMessage = error?.message || "";
        state.loginEmailSent = false;
        state.state = ReducerStates.FAILED;
      })
      .addCase(emailLogin.pending, (state) => {
        state.state = ReducerStates.PENDING;
        state.errorCode = initialLoginState.errorCode;
        state.errorMessage = initialLoginState.errorMessage;
      })
      .addCase(emailLogin.fulfilled, (state) => {
        state.state = ReducerStates.SUCCEEDED;
        state.loginEmailSent = true;
      })
      .addCase(emailLogin.rejected, (state, action) => {
        const error = (action.error as unknown) as IFailedRequest;
        state.errorCode = error?.code || "";
        state.errorMessage = error?.message || "";
        state.state = ReducerStates.FAILED;
      })
      .addCase(clearSession.type, (state) => {
        return initialLoginState;
      })
      .addCase(initialize.fulfilled, (state, action) => {
        if (action.payload.subData !== null) {
          state.isLogged = true;
        } else {
          state.isLogged = false;
        }
        state.loginEmailSent = false;
      });
  },
});

const { reducer } = loginSlice;

// Destructure and export the plain action creators
export const {
  logout,
  resetSliceState,
  authorizeMode,
  setFrameOrigin,
  subscriptionManagementInit,
  setLoginEmail,
} = loginSlice.actions;

export default reducer;
